import { Box, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Contract_Status_Enum, OrderRecommendation } from '@kheops/graphql';
import OrderRecommendationsBgXs from '../../assets/images/order-recommendations-xs.png';
import OrderRecommendationsBgSmMd from '../../assets/images/order-recommendations-sm-md.png';
import OrderRecommendationsBgLgXl from '../../assets/images/order-recommendations-lg-xl.png';
import { useOrderRecommendationsQuery } from '../../queries/__generated__/orderRecommendations.generated';
import useCurrentBreakpoint from '../../hooks/useCurrentBreakpoint';
import { currentContextAtom, signedContractsAtom } from '../../state';
import { selectedRecommendationsAtom } from '../state/state';
import OrderRecommendationCard from './OrderRecommendationCard';

export const OrderRecommendationsBackgrounds = {
  xs: OrderRecommendationsBgXs,
  sm: OrderRecommendationsBgSmMd,
  md: OrderRecommendationsBgSmMd,
  lg: OrderRecommendationsBgLgXl,
  xl: OrderRecommendationsBgLgXl,
};

export default function OrderRecommendations(): React.JSX.Element {
  const { t } = useTranslation(['basket']);
  const { companyId } = useAtomValue(currentContextAtom);
  const theme = useTheme();
  const isUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'));
  const breakpoint = useCurrentBreakpoint();
  const contracts = useAtomValue(signedContractsAtom);
  const { data } = useOrderRecommendationsQuery({ variables: { buyingCompanyId: companyId }, fetchPolicy: 'network-only' });
  const [selectedRecommendations, setSelectedRecommendations] = useAtom(selectedRecommendationsAtom);

  const width = isUpSm ? 332 : 237;

  const reorderRecommendations = useCallback((recommendation: OrderRecommendation) => {
    const alreadySelectedIndex = selectedRecommendations.findIndex(({ contractReferenceId }) => contractReferenceId === recommendation.contractReferenceId);

    if (alreadySelectedIndex !== -1) {
      setSelectedRecommendations(selectedRecommendations.toSpliced(alreadySelectedIndex, 1).concat([recommendation]));
    } else {
      setSelectedRecommendations(selectedRecommendations.concat([recommendation]));
    }
  }, [selectedRecommendations]);

  const recommendations = useMemo(() => {
    const fetchedRecommendations = data?.getOrderRecommendations?.recommendations;

    if (!fetchedRecommendations) {
      return;
    }

    return fetchedRecommendations
      .filter((recommendation) => {
        const hasSignedContract = contracts.some(
          ({ reference_id, status }) => reference_id === recommendation!.contractReferenceId && status === Contract_Status_Enum.Signed,
        );

        return hasSignedContract && !selectedRecommendations.some(({ contractReferenceId }) => contractReferenceId === recommendation!.contractReferenceId);
      })
      .concat(selectedRecommendations);
  }, [data?.getOrderRecommendations?.recommendations, selectedRecommendations]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!!recommendations?.length && (
        <Box
          sx={{
            background: `url(${OrderRecommendationsBackgrounds[breakpoint]}) lightgray 100% / cover no-repeat`,
            borderRadius: 6,
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant="headlineSmall">
            {t('basket:order_again')}
          </Typography>
          <Tabs
            sx={{
              mt: 2,
              '& .MuiTabs-list': {
                gap: 2,
              },
            }}
            variant="scrollable"
            scrollButtons={isUpMd}
          >
            {recommendations.map((recommendation) => (
              <Tab
                key={recommendation!.id}
                component={OrderRecommendationCard}
                recommendation={recommendation!}
                onAddToBasket={() => reorderRecommendations(recommendation!)}
                sx={{
                  p: 0,
                }}
                sxCard={{
                  width,
                  mx: 1,
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}
    </>
  );
}
